import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ROLES, can } from '@pl/domain';
import type { NewUserDto, Role, SiteDto, TempPasswordDto, UserDto, UserPatch } from '@pl/domain';
import { type FormEvent, useId, useMemo, useRef, useState } from 'react';
import { LINKS_REMOVED } from '../../auth/ChangePassword';
import { api } from '../../lib/api';
import { shortDateTime } from '../../lib/format';
import { qk, useMe, useSites, useUsers } from '../../lib/queries';
import { Chip, Empty, ErrorNote, RefetchNote, SiteTag, Spinner } from '../../ui/bits';
import { Modal } from '../../ui/Dialog';
import { Icon } from '../../ui/Icon';
import s from './SettingsUsers.module.css';

/** Each role in a line (spec §8), shown beside its name wherever a role is picked. */
const ROLE_INFO: Record<Role, { label: string; line: string }> = {
  worker: { label: 'Worker', line: 'scans at their sites' },
  lead: { label: 'Lead', line: 'a worker who can also override, write off and resolve' },
  office: { label: 'Office', line: 'orders, stickers, stock and imports; no scanning' },
  admin: { label: 'Admin', line: 'everything, including Settings and users' },
};

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Open = { kind: 'add' } | { kind: 'edit' | 'active' | 'reset'; user: UserDto };

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('');
}

/**
 * An address that may break after its `@` and nowhere else it can help: the name column is the one
 * that gives way when the table is squeezed at 1280 px, and "dee@" over "tscables.test" reads better
 * than a word cut in half.
 */
function Email({ address }: { address: string }) {
  const at = address.lastIndexOf('@');
  if (at < 0) return <small>{address}</small>;
  return (
    <small>
      {address.slice(0, at + 1)}
      <wbr />
      {address.slice(at + 1)}
    </small>
  );
}

const sameSites = (x: string[] | null, y: string[] | null) =>
  x === null || y === null ? x === y : x.length === y.length && x.every((id) => y.includes(id));

/** `mockups/office-10-settings-devices.html` for the panel and table: Settings → Users. */
export function SettingsUsers() {
  const meQuery = useMe();
  const me = meQuery.data;
  const canManage = me ? can(me.role, 'settings') : false;
  const usersQuery = useUsers(canManage);
  const sitesQuery = useSites();
  const [open, setOpen] = useState<Open | null>(null);
  const sites = useMemo(() => sitesQuery.data ?? [], [sitesQuery.data]);
  const siteById = useMemo(() => new Map(sites.map((x) => [x.id, x])), [sites]);

  if (!me) return <Spinner label="Loading users" />;
  if (!canManage)
    return (
      <div className="panel">
        <Empty title="Only an admin can manage users" description="An admin adds people here, sets what each of them can do, and resets forgotten passwords." />
      </div>
    );

  const users = usersQuery.data ?? [];
  const activeCount = users.filter((u) => u.active).length;
  const close = () => setOpen(null);

  return (
    <div className="panel">
      <div className="panel-h">
        <h2>Users</h2>
        {usersQuery.data && (
          <Chip>
            {activeCount} active{users.length > activeCount ? ` · ${users.length - activeCount} deactivated` : ''}
          </Chip>
        )}
        <span className="grow" />
        <span className="sub">Everyone signs in as themselves, so every scan names who made it</span>
        <button type="button" className="btn primary sm" onClick={() => setOpen({ kind: 'add' })}>
          <Icon name="plus" />
          Add user
        </button>
      </div>

      <RefetchNote queries={[usersQuery]} className={s.usNote} />
      {usersQuery.isLoadingError ? (
        <div className={s.usNote}>
          <ErrorNote error={usersQuery.error} />
          <button type="button" className="btn" onClick={() => usersQuery.refetch()}>
            Try again
          </button>
        </div>
      ) : usersQuery.isPending ? (
        <div className="panel-b">
          <Spinner label="Loading users" />
        </div>
      ) : (
        <table className={`t ${s.usTable}`}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Role</th>
              <th>Sites</th>
              <th>Status</th>
              <th className="r">
                <span className={s.usHidden}>Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className={u.active ? undefined : s.usOff}>
                <td>
                  <div className={s.usName}>
                    <span className={s.usAvatar} aria-hidden="true">
                      {initials(u.name)}
                    </span>
                    <span className={s.usWho}>
                      <span className={s.usNameLine}>
                        <b>{u.name}</b>
                        {u.id === me.user.id && <span className={s.usYou}>You</span>}
                      </span>
                      <Email address={u.email} />
                    </span>
                  </div>
                </td>
                <td>
                  <Chip tone={u.role === 'admin' ? 'ink' : undefined}>{ROLE_INFO[u.role].label}</Chip>
                </td>
                <td>
                  {u.siteIds === null ? (
                    <span className={`muted ${s.usAll}`}>All sites</span>
                  ) : (
                    <span className={s.usSites}>
                      {u.siteIds.map((id) => (
                        <SiteTag key={id} site={siteById.get(id)} />
                      ))}
                    </span>
                  )}
                </td>
                <td>
                  <div className={s.usStatus}>
                    {u.active ? (
                      <Chip tone="ok" dot>
                        Active
                      </Chip>
                    ) : (
                      <Chip>Deactivated</Chip>
                    )}
                    {/* A deactivated user's sessions are deleted, so "never" would be untrue for them. */}
                    {u.active && <small>{u.lastSeenAt ? `Seen ${shortDateTime(u.lastSeenAt)}` : 'Never signed in'}</small>}
                  </div>
                </td>
                <td className="r">
                  <div className={s.usActions}>
                    <button type="button" className="btn sm" aria-label={`Edit ${u.name}`} onClick={() => setOpen({ kind: 'edit', user: u })}>
                      Edit
                    </button>
                    <button type="button" className="btn sm" aria-label={`Reset password for ${u.name}`} onClick={() => setOpen({ kind: 'reset', user: u })}>
                      Reset password
                    </button>
                    {u.active ? (
                      <button type="button" className="btn sm danger" aria-label={`Deactivate ${u.name}`} onClick={() => setOpen({ kind: 'active', user: u })}>
                        Deactivate
                      </button>
                    ) : (
                      <button type="button" className="btn sm" aria-label={`Reactivate ${u.name}`} onClick={() => setOpen({ kind: 'active', user: u })}>
                        Reactivate
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {open?.kind === 'add' && <UserModal user={null} sites={sites} isMe={false} onClose={close} />}
      {open?.kind === 'edit' && <UserModal user={open.user} sites={sites} isMe={open.user.id === me.user.id} onClose={close} />}
      {open?.kind === 'active' && <ActiveModal user={open.user} isMe={open.user.id === me.user.id} onClose={close} />}
      {open?.kind === 'reset' && <ResetModal user={open.user} isMe={open.user.id === me.user.id} onClose={close} />}
    </div>
  );
}

/**
 * The one sight of a temporary password. It lives in this component and the mutation that fetched
 * it, both of which are gone when the dialog closes (the mutations use `gcTime: 0`), so nothing on
 * the page can show it again.
 */
function TempPassword({ name, password }: { name: string; password: string }) {
  const [copied, setCopied] = useState<'yes' | 'select' | null>(null);
  const code = useRef<HTMLElement>(null);

  async function copy() {
    try {
      await navigator.clipboard.writeText(password);
      setCopied('yes');
    } catch {
      // No clipboard (a page served over plain http has none): select it, so Ctrl+C does the rest.
      if (code.current) window.getSelection()?.selectAllChildren(code.current);
      setCopied('select');
    }
  }

  return (
    <div className={s.usTemp}>
      <span className={s.usTempK}>{name} signs in with</span>
      <div className={s.usTempRow}>
        <code ref={code} className={s.usTempValue}>
          {password}
        </code>
        <button type="button" className="btn sm" onClick={() => void copy()}>
          <Icon name={copied === 'yes' ? 'check' : 'link'} />
          {copied === 'yes' ? 'Copied' : 'Copy'}
        </button>
      </div>
      {copied === 'select' && <small className={s.usHelp}>Selected. Press Ctrl+C to copy it.</small>}
      <p className={s.usWarn}>
        <Icon name="lock" />
        Write this down now. It will not be shown again.
      </p>
    </div>
  );
}

type Saved = { kind: 'created'; result: NewUserDto } | { kind: 'saved' };

/** Add a user, or edit one. Adding ends on their temporary password; editing sends only what changed. */
function UserModal({ user, sites, isMe, onClose }: { user: UserDto | null; sites: SiteDto[]; isMe: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const id = useId();
  const [email, setEmail] = useState(user?.email ?? '');
  const [name, setName] = useState(user?.name ?? '');
  const [role, setRole] = useState<Role>(user?.role ?? 'worker');
  const [everySite, setEverySite] = useState(user ? user.siteIds === null : true);
  // Ids, not just the sites listed: a site that has since been deactivated is not listed, and must
  // not fall off someone's access because their name was edited.
  const [chosen, setChosen] = useState<string[]>(user?.siteIds ?? []);
  const siteIds = everySite ? null : chosen;

  const save = useMutation({
    mutationFn: async (): Promise<Saved> => {
      if (!user) return { kind: 'created', result: await api.post<NewUserDto>('/users', { email: email.trim(), name: name.trim(), role, siteIds }) };
      const patch: UserPatch = {};
      if (name.trim() !== user.name) patch.name = name.trim();
      if (role !== user.role) patch.role = role;
      if (!sameSites(siteIds, user.siteIds)) patch.siteIds = siteIds;
      if (Object.keys(patch).length > 0) await api.patch<UserDto>(`/users/${user.id}`, patch);
      return { kind: 'saved' };
    },
    gcTime: 0,
    onSuccess: (saved) => {
      void qc.invalidateQueries({ queryKey: qk.users });
      // Their own role may have changed, and with it what this page may show them.
      if (isMe) void qc.invalidateQueries({ queryKey: qk.me });
      if (saved.kind === 'saved') onClose();
    },
  });
  const created = save.data?.kind === 'created' ? save.data.result : null;

  const emailOk = EMAIL.test(email.trim());
  const canSave = Boolean((user || emailOk) && name.trim() && (everySite || chosen.length > 0) && !save.isPending);

  function edited() {
    // An error names the form as it was sent; once it changes, the error is about something else.
    if (save.isError) save.reset();
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    if (canSave) save.mutate();
  }

  const title = created ? `Temporary password for ${created.user.name}` : user ? `Edit ${user.name}` : 'Add user';

  return (
    <Modal
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={title}
      // The password is on screen once; a stray click or Escape must not lose it. Only Done closes.
      dismissible={!created}
      footer={
        created ? (
          <>
            <span className="sub">Give it to them in person or by phone</span>
            <span className="grow" />
            <button type="button" className="btn primary" onClick={onClose}>
              Done
            </button>
          </>
        ) : (
          <>
            <span className="sub">{user ? (isMe ? 'This is you' : 'Takes effect on their next request') : 'They get a temporary password'}</span>
            <span className="grow" />
            <button type="button" className="btn ghost" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" form={`${id}-form`} className="btn primary" disabled={!canSave}>
              <Icon name={user ? 'check' : 'plus'} />
              {user ? 'Save changes' : 'Create user'}
            </button>
          </>
        )
      }
    >
      {created ? (
        <TempPassword name={created.user.name} password={created.tempPassword} />
      ) : (
        <form id={`${id}-form`} className={s.usForm} onSubmit={submit} noValidate>
          {save.isError && <ErrorNote error={save.error} />}
          <div className="fld">
            <label htmlFor={`${id}-email`}>Email</label>
            <input
              id={`${id}-email`}
              type="email"
              className={`inp ${user ? 'off' : ''}`}
              autoFocus={!user}
              readOnly={Boolean(user)}
              value={email}
              maxLength={320}
              autoComplete="off"
              spellCheck={false}
              aria-describedby={`${id}-email-help`}
              onChange={(e) => {
                setEmail(e.target.value);
                edited();
              }}
            />
            {/* A <small>, not a <span>: `.fld > span` is the field caption's style. */}
            <small id={`${id}-email-help`} className={s.usHelp}>
              {user ? 'The address they sign in with. It can’t be changed.' : 'The address they sign in with.'}
            </small>
          </div>
          <div className="fld">
            <label htmlFor={`${id}-name`}>Name</label>
            <input
              id={`${id}-name`}
              className="inp"
              autoFocus={Boolean(user)}
              value={name}
              maxLength={120}
              autoComplete="off"
              onChange={(e) => {
                setName(e.target.value);
                edited();
              }}
            />
          </div>
          <div className="fld">
            <label htmlFor={`${id}-role`}>Role</label>
            <select
              id={`${id}-role`}
              className={`inp ${s.usSelect}`}
              value={role}
              onChange={(e) => {
                setRole(e.target.value as Role);
                edited();
              }}
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {ROLE_INFO[r].label} — {ROLE_INFO[r].line}
                </option>
              ))}
            </select>
          </div>
          <fieldset className={s.usFieldset}>
            <legend>Site access</legend>
            <div className={s.usRadios}>
              <label className={s.usChoice}>
                <input
                  type="radio"
                  name={`${id}-scope`}
                  checked={everySite}
                  onChange={() => {
                    setEverySite(true);
                    edited();
                  }}
                />
                All sites
              </label>
              <label className={s.usChoice}>
                <input
                  type="radio"
                  name={`${id}-scope`}
                  checked={!everySite}
                  onChange={() => {
                    setEverySite(false);
                    edited();
                  }}
                />
                Only these sites
              </label>
            </div>
            <div className={s.usSiteList}>
              {sites.map((x) => (
                <label key={x.id} className={`${s.usChoice} ${everySite ? s.usChoiceOff : ''}`}>
                  <input
                    type="checkbox"
                    disabled={everySite}
                    checked={everySite || chosen.includes(x.id)}
                    onChange={(e) => {
                      setChosen((c) => (e.target.checked ? [...c, x.id] : c.filter((v) => v !== x.id)));
                      edited();
                    }}
                  />
                  {x.name}
                  {x.live ? '' : ' (not live)'}
                </label>
              ))}
            </div>
            {!everySite && chosen.length === 0 && <small className={s.usHelp}>Pick at least one site, or All sites.</small>}
          </fieldset>
        </form>
      )}
    </Modal>
  );
}

/** Deactivate or reactivate one user, behind a line that says what it does. */
function ActiveModal({ user, isMe, onClose }: { user: UserDto; isMe: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const deactivating = user.active;
  const change = useMutation({
    mutationFn: () => api.patch<UserDto>(`/users/${user.id}`, { active: !user.active }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.users });
      if (isMe) void qc.invalidateQueries({ queryKey: qk.me });
      onClose();
    },
  });
  const verb = deactivating ? 'Deactivate' : 'Reactivate';
  return (
    <Modal
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={`${verb} ${user.name}?`}
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={deactivating ? 'btn danger' : 'btn primary'} disabled={change.isPending} onClick={() => change.mutate()}>
            {verb}
          </button>
        </>
      }
    >
      {change.isError && <ErrorNote error={change.error} />}
      {deactivating ? (
        <>
          <p className={s.usConfirm}>
            {isMe ? 'You will be signed out now and will not be able to sign in again. Your scan history stays.' : `${user.name} will not be able to sign in. Their scan history stays.`}
          </p>
          <p className={s.usConfirmSub}>{isMe ? 'Another admin can reactivate you.' : 'They are signed out of every phone and browser at once.'}</p>
        </>
      ) : (
        <p className={s.usConfirm}>{user.name} will be able to sign in again, with the password they had.</p>
      )}
    </Modal>
  );
}

/** Reset one user's password, behind a confirmation, ending on the new temporary password. */
function ResetModal({ user, isMe, onClose }: { user: UserDto; isMe: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const reset = useMutation({
    mutationFn: () => api.post<TempPasswordDto>(`/users/${user.id}/reset-password`),
    gcTime: 0,
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.users }),
  });
  const done = reset.data;
  return (
    <Modal
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={done ? `Temporary password for ${user.name}` : `Reset ${user.name}’s password?`}
      // As on adding a user: once the password is on screen, only Done closes it.
      dismissible={!done}
      footer={
        done ? (
          <>
            <span className="sub">Give it to them in person or by phone</span>
            <span className="grow" />
            <button type="button" className="btn primary" onClick={onClose}>
              Done
            </button>
          </>
        ) : (
          <>
            <span className="grow" />
            <button type="button" className="btn ghost" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="btn primary" disabled={reset.isPending} onClick={() => reset.mutate()}>
              Reset password
            </button>
          </>
        )
      }
    >
      {done ? (
        <>
          <TempPassword name={user.name} password={done.tempPassword} />
          {done.whatsappLinksRemoved > 0 && <p className={s.usConfirmSub}>{LINKS_REMOVED}</p>}
        </>
      ) : (
        <>
          {reset.isError && <ErrorNote error={reset.error} />}
          <p className={s.usConfirm}>
            {isMe
              ? 'Your current password stops working, and you are signed out everywhere but here. You’ll get a temporary password to sign in with next time.'
              : `${user.name}’s current password stops working, and they are signed out everywhere. You’ll get a temporary password to give them.`}
          </p>
        </>
      )}
    </Modal>
  );
}
